import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, type EstadoLicenca, type SessaoDemo } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';

const CHAVE_DEMO = 'miolos.demo';

export interface EstadoAcesso {
  /** Carregando a situação da licença/demonstração. */
  carregando: boolean;
  /** As páginas exportadas precisam sair com marca d'água. */
  marcaDagua: boolean;
  /** Texto principal da marcação. */
  textoMarcaDagua: string;
  /** Linha menor, com o e-mail de quem gerou o arquivo. */
  detalheMarcaDagua: string | null;
  /** Existe uma sessão de demonstração liberada por e-mail neste navegador. */
  demo: SessaoDemo | null;
  /** O administrador bloqueou este acesso. */
  bloqueado: boolean;
  licenca: EstadoLicenca | null;
  guardarDemo: (sessao: SessaoDemo) => void;
  limparDemo: () => void;
  recarregar: () => Promise<void>;
}

const Contexto = createContext<EstadoAcesso | null>(null);

/** Marca d'água do material de demonstração. */
export const TEXTO_MARCA_DAGUA = 'Cópia não autorizada';
export const DETALHE_MARCA_DAGUA = 'Demonstração · uso pessoal e não comercial';

function lerDemoSalva(): SessaoDemo | null {
  try {
    const bruto = window.localStorage.getItem(CHAVE_DEMO);
    if (!bruto) return null;
    const dados = JSON.parse(bruto) as SessaoDemo;
    if (!dados?.email || !dados?.expiraEm) return null;
    if (new Date(dados.expiraEm).getTime() < Date.now()) return null;
    return dados;
  } catch {
    return null;
  }
}

export function ProvedorAcesso({ children }: { children: ReactNode }) {
  const { usuario } = useAuth();
  const idDoUsuario = usuario?.id ?? '';
  const [licenca, setLicenca] = useState<EstadoLicenca | null>(null);
  const [demo, setDemo] = useState<SessaoDemo | null>(() => lerDemoSalva());
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    if (!idDoUsuario) {
      setLicenca(null);
      setCarregando(false);
      return;
    }
    try {
      setLicenca(await api.minhaLicenca());
    } catch {
      setLicenca(null);
    } finally {
      setCarregando(false);
    }
    /*
     * A dependência é o **id** da conta, e não o objeto `usuario`: reler o perfil
     * devolve um objeto novo a cada chamada, e um efeito preso a ele dispara uma
     * consulta de licença a cada releitura — foi assim que uma aba aberta chegou
     * a pedir a licença seiscentas vezes e o IP acabou banido pelo limite da API.
     */
  }, [idDoUsuario]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * Quem entra na conta deixa de ser visitante de demonstração.
   *
   * A marca d'água da demo é decidida pela licença da conta logada, então a
   * sessão guardada no navegador (de quem testou antes de assinar) é descartada
   * — senão um assinante ou o administrador continuariam imprimindo com a marca
   * “cópia não autorizada” só por terem testado a demo neste navegador.
   */
  useEffect(() => {
    if (!usuario) return;
    setDemo((atual) => {
      if (!atual) return atual;
      try {
        window.localStorage.removeItem(CHAVE_DEMO);
      } catch {
        /* navegador em modo privado: nada a limpar */
      }
      return null;
    });
  }, [usuario]);

  const guardarDemo = useCallback((sessao: SessaoDemo) => {
    setDemo(sessao);
    try {
      window.localStorage.setItem(CHAVE_DEMO, JSON.stringify(sessao));
    } catch {
      /* navegador em modo privado: a sessão vale só nesta aba */
    }
  }, []);

  const limparDemo = useCallback(() => {
    setDemo(null);
    try {
      window.localStorage.removeItem(CHAVE_DEMO);
    } catch {
      /* nada a fazer */
    }
  }, []);

  /**
   * Regra do produto: sem licença paga ativa, tudo sai com marca d'água.
   *
   * Quem está logado é julgado **pela licença da conta** — administrador e
   * assinante imprimem limpo. A sessão de demonstração só vale para visitante,
   * que é justamente quem não tem conta: assim a demo antiga guardada no
   * navegador não marca o trabalho de quem depois assinou.
   */
  const marcaDagua = usuario ? licenca?.marcaDagua !== false : demo !== null || licenca?.marcaDagua !== false;

  const detalhe = useMemo(() => {
    const quem = usuario?.email ?? demo?.email;
    return quem ? `${DETALHE_MARCA_DAGUA} · ${quem}` : DETALHE_MARCA_DAGUA;
  }, [usuario?.email, demo?.email]);

  const valor = useMemo<EstadoAcesso>(
    () => ({
      carregando,
      marcaDagua,
      textoMarcaDagua: TEXTO_MARCA_DAGUA,
      detalheMarcaDagua: detalhe,
      demo,
      bloqueado: licenca?.bloqueado === true,
      licenca,
      guardarDemo,
      limparDemo,
      recarregar: carregar,
    }),
    [carregando, marcaDagua, detalhe, demo, licenca, guardarDemo, limparDemo, carregar],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useAcesso(): EstadoAcesso {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useAcesso precisa estar dentro de <ProvedorAcesso>.');
  return contexto;
}
